import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "../../context/CartContext";

const links = [
  { label: "Store", href: "#store" },
  { label: "The Standard", href: "#how" },
  { label: "Hall of Fame", href: "#winners" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    if (mobile) {
      // Close first, then scroll after the collapse animation finishes —
      // the layout shift from the menu closing would otherwise cancel the
      // smooth scroll mid-flight.
      setMobile(false);
      setTimeout(() => {
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
      }, 350);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-300 ${
        scrolled
          ? "bg-[#ECECE8]/90 backdrop-blur-md border-b-2 border-[#111111]"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button onClick={() => go("#top")} data-testid="nav-home" className="cursor-pointer">
          <Logo />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className="text-xs font-semibold uppercase tracking-[0.15em] text-[#111111]/70 hover:text-[#FF4500] transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setOpen(true)}
            data-testid="nav-cart-button"
            className="relative flex items-center gap-2 bg-[#FF4500] text-white px-4 py-2.5 rounded-sm font-bold text-xs uppercase tracking-wider hover:bg-[#111111] transition-colors duration-200"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-2 -right-2 bg-[#111111] text-[#FF4500] border border-[#FF4500] text-[10px] font-bold h-5 min-w-5 px-1 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="md:hidden text-[#111111]"
            onClick={() => setMobile((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {mobile ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobile && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#ECECE8] border-b-2 border-[#111111]"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  data-testid={`nav-mobile-${l.href.replace("#", "")}`}
                  className="text-left py-3 text-sm font-semibold uppercase tracking-wider text-[#111111]/80 hover:text-[#FF4500]"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
